/* eslint-disable camelcase */
import { ApolloError, gql, useMutation } from '@apollo/client';
import AccountsSDK from '@livechat/accounts-sdk';
import Cookies from 'js-cookie';

import Config from 'Config';
import { useNotifications } from 'contexts/notifications/use-notifications';
import { getCanJoinProduct } from 'hooks/use-can-join-to-product';
import { useGetMe } from 'hooks/use-get-me';
import { fetchAccounts } from 'hooks/use-accounts';
import Amplitude from 'services/Amplitude';
import { getAccessToken } from 'helpers/account';
import { fetchFeatures } from 'hooks/use-fetch-features';
import { fetchSubscriptions } from 'hooks/use-fetch-subscriptions';
import { getQueryClient } from 'services/ReactQueryClient';

import { useJoinProduct } from './use-join-product';

const JOIN_ORGANIZATION_MUTATION = gql`
  mutation JoinOrganization($organization_id: ID!) {
    JoinOrganization(organization_id: $organization_id)
  }
`;

type Props = {
  organization_id: string;
  product: string;
  onJoinBlocked(organizationId: string): void;
};

type OrganizationAndProduct = {
  handleJoinProduct(): void;
  isLoading: boolean;
};

export const useJoinOrganizationAndProduct = ({
  organization_id,
  product,
  onJoinBlocked,
}: Props): OrganizationAndProduct => {
  const { showToast } = useNotifications();
  const { email } = useGetMe();
  const { handleJoin, isLoading: isJoiningProduct } = useJoinProduct({
    organization_id,
    product,
  });

  const handleError = (error: ApolloError): void => {
    let err;

    try {
      const errorDescription = error.graphQLErrors[0]?.extensions?.description as string;
      const stringfiedError = errorDescription?.split(' - ')[1];

      err = stringfiedError ? JSON.parse(stringfiedError) : null;
    } catch (e) {
      console.error(e);
    }

    showToast({
      content: err?.error?.message ?? 'Joining to a selected product failed',
      variant: 'error',
    });
  };

  const [joinOrganization, { loading: isJoiningOrganization }] = useMutation<boolean>(
    JOIN_ORGANIZATION_MUTATION,
    {
      context: {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
      onError: handleError,
      onCompleted: async () => {
        const sdk = new AccountsSDK({
          client_id: Config.organization_app_client_id,
          server_url: Config.accounts_url,
          organization_id,
        });
        const response = await sdk.iframe().authorize();
        Cookies.set('access_token', response.access_token, { secure: true, sameSite: 'none' });
        getQueryClient().invalidateQueries(['get-accounts']);
        getQueryClient().invalidateQueries(['get-me']);

        if (onJoinBlocked) {
          const [features, subscriptions, accounts] = await Promise.all([
            fetchFeatures(response.access_token),
            fetchSubscriptions(response.access_token),
            fetchAccounts(response.access_token),
          ]);

          const { joiningBlocked, isLCStarterPlan } = getCanJoinProduct({
            product,
            accounts,
            features,
            subscriptions: subscriptions?.subscriptions,
          });

          if (joiningBlocked || isLCStarterPlan) {
            onJoinBlocked(organization_id);
            return;
          }
        }

        handleJoin(response.access_token);
      },
    }
  );

  const handleJoinProduct = (): void => {
    joinOrganization({ variables: { organization_id } });
    Amplitude.logEvent(email, 'JOIN_PRODUCT_CLICK', { product });
  };

  return {
    handleJoinProduct,
    isLoading: isJoiningOrganization || isJoiningProduct,
  };
};
